import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    ClientStoreSchema,
    ClientListStoresQueryParams,
    CreateStoreRequest,
    UpdateStoreRequest,
} from "../schemas/stores-v2";

export function createClientStoresV2Api(http: HttpClient, baseUrl: string) {
    const clientBaseUrl = `${baseUrl}/client/stores`;

    return {
        /**
         * List all stores belonging to the authenticated client
         * GET /api/v2/client/stores
         */
        listStores: createEndpointWithPagination<
            z.infer<typeof ClientStoreSchema>,
            unknown,
            unknown,
            ClientListStoresQueryParams
        >(http, "GET", "", {
            base: clientBaseUrl,
            itemSchema: ClientStoreSchema,
        }),

        /**
         * Get a single store by ID (must belong to authenticated client)
         * GET /api/v2/client/stores/:id
         */
        getStore: createEndpoint<
            z.infer<typeof ClientStoreSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: clientBaseUrl,
            responseSchema: ClientStoreSchema,
            params: ["id"],
        }),

        /**
         * Create a new store for the authenticated client
         * POST /api/v2/client/stores
         */
        createStore: createEndpoint<
            z.infer<typeof ClientStoreSchema>,
            CreateStoreRequest,
            QueryParams
        >(http, "POST", "", {
            base: clientBaseUrl,
            responseSchema: ClientStoreSchema,
        }),

        /**
         * Update an existing store
         * PUT /api/v2/client/stores/:id
         */
        updateStore: createEndpoint<
            z.infer<typeof ClientStoreSchema>,
            UpdateStoreRequest,
            QueryParams,
            { id: number }
        >(http, "PUT", "/:id", {
            base: clientBaseUrl,
            responseSchema: ClientStoreSchema,
            params: ["id"],
        }),

        /**
         * Delete a store (soft-delete)
         * DELETE /api/v2/client/stores/:id
         */
        deleteStore: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "DELETE", "/:id", {
            base: clientBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["id"],
        }),
    };
}

export type ClientStoresV2Api = ReturnType<typeof createClientStoresV2Api>;

